/**
 * 会員情報の変更: 入力 → 確認（静的ビルド用 sessionStorage）
 * 入力 PC4100-18919 / SP 4100-18771 / 確認 PC 4100-18990 / 完了 PC 4100-18836
 */
import { validateMemberEditForm, initMemberEditFormValidation } from './member-edit-form-validation.js';

const STORAGE_KEY = 'takamatsu_member_edit_v1';
const CONFIRM_PATH = '/member_edit_confirm.html';
const COMPLETE_PATH = '/member_edit_complete.html';

/** TODO: デザイン確認用。本番では false にし、データなし時は empty を表示 */
const MEMBER_EDIT_CONFIRM_PREVIEW_ALWAYS_VISIBLE = true;

function getForm() {
	return document.querySelector('form[data-member-edit-flow]#member-edit-form');
}

/** 「修正する」で戻ったとき用の生フィールド（会員登録・資料請求と同役） */
function collectRawFormFields(form) {
	const fd = new FormData(form);
	const pick = (name) => (fd.get(name) ?? '').toString();
	return {
		member_edit_name_family: pick('member_edit_name_family').trim(),
		member_edit_name_given: pick('member_edit_name_given').trim(),
		member_edit_email: pick('member_edit_email').trim(),
		member_edit_password: pick('member_edit_password'),
		member_edit_password_confirm: pick('member_edit_password_confirm'),
		member_edit_tel: pick('member_edit_tel').trim(),
		member_edit_zip: pick('member_edit_zip').trim(),
		member_edit_pref: pick('member_edit_pref').trim(),
		member_edit_city: pick('member_edit_city').trim(),
		member_edit_street: pick('member_edit_street').trim(),
		member_edit_building: pick('member_edit_building').trim(),
	};
}

function collectPayload(form) {
	const fd = new FormData(form);
	const family = (fd.get('member_edit_name_family') || '').toString().trim();
	const given = (fd.get('member_edit_name_given') || '').toString().trim();
	const nameFull = [family, given].filter(Boolean).join(' ') || '—';
	const pwd = (fd.get('member_edit_password') || '').toString().trim();
	return {
		member_name_full: nameFull,
		member_email: (fd.get('member_edit_email') || '').toString().trim() || '—',
		member_tel: (fd.get('member_edit_tel') || '').toString().trim() || '—',
		password_will_change: Boolean(pwd),
	};
}

function saveDraft(payload) {
	try {
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
	} catch {
		/* ignore */
	}
}

function loadDraft() {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

function clearDraft() {
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch {
		/* ignore */
	}
}

function applyRestoreToForm(form, raw) {
	if (!raw || typeof raw !== 'object') return;

	for (const key of Object.keys(raw)) {
		const val = raw[key];
		const str = val == null ? '' : String(val);
		const el = form.elements.namedItem(key);
		if (!el || el instanceof RadioNodeList) continue;
		if (el.tagName === 'SELECT' || el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
			el.value = str;
		}
	}
}

function setText(el, text) {
	if (!el) return;
	el.textContent = text;
}

function initFormPage() {
	const form = getForm();
	if (!form) return;

	initMemberEditFormValidation(form);

	const draft = loadDraft();
	if (draft && draft._restore && typeof draft._restore === 'object') {
		applyRestoreToForm(form, draft._restore);
	}

	form.addEventListener('submit', (e) => {
		e.preventDefault();
		if (!validateMemberEditForm(form)) {
			return;
		}
		const payload = collectPayload(form);
		const raw = collectRawFormFields(form);
		saveDraft({ ...payload, _restore: raw });
		window.location.href = CONFIRM_PATH;
	});
}

function initConfirmPage() {
	const page = document.querySelector('[data-member-edit-page="confirm"]');
	if (!page) return;

	const block = document.getElementById('member-edit-confirm-block');
	const empty = document.getElementById('member-edit-confirm-empty');
	const draft = loadDraft();

	if (!block || !empty) return;

	const sendBtn = document.getElementById('member-edit-confirm-send');
	if (sendBtn) {
		sendBtn.addEventListener('click', () => {
			clearDraft();
			window.location.assign(COMPLETE_PATH);
		});
	}

	if (!draft) {
		if (MEMBER_EDIT_CONFIRM_PREVIEW_ALWAYS_VISIBLE) {
			block.hidden = false;
			empty.hidden = true;
		} else {
			empty.hidden = false;
			block.hidden = true;
		}
		return;
	}

	const hasRequired =
		draft.member_name_full !== '—' &&
		draft.member_email !== '—' &&
		draft.member_email.includes('@');

	if (!hasRequired) {
		if (MEMBER_EDIT_CONFIRM_PREVIEW_ALWAYS_VISIBLE) {
			block.hidden = false;
			empty.hidden = true;
		} else {
			empty.hidden = false;
			block.hidden = true;
		}
		return;
	}

	block.hidden = false;
	empty.hidden = true;

	page.querySelectorAll('[data-member-edit-field]').forEach((el) => {
		const key = el.getAttribute('data-member-edit-field');
		if (!key || !(key in draft)) return;
		setText(el, String(draft[key]));
	});

	const pwdMsg = document.getElementById('member-edit-confirm-password-msg');
	if (pwdMsg) {
		setText(
			pwdMsg,
			draft.password_will_change
				? 'セキュリティのため表示していません。ご入力の内容で更新されます。'
				: '変更しません。',
		);
	}
}

export function initMemberEditFlow() {
	const page = document.querySelector('[data-member-edit-page]')?.getAttribute('data-member-edit-page');
	if (!page) return;
	if (page === 'form') initFormPage();
	if (page === 'confirm') initConfirmPage();
}
